import {
  getBackLink,
  getBreadcrumbComponent,
  getBreadcrumbData,
  getBreadcrumbSecondLastLink,
} from '@/features/Breadcrumb/shared/Breadcrumb.utils';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { useElasticRelated } from '@/features/shared/Hooks';
import { MAX_RELATED_CONTENT } from '@/utils/constants';
import { CTADocumentMapping, MetadataDocumentMapping } from '@/utils/mappings';
import {
  resolveBool,
  resolveColorName,
  resolveContentBlocksContainer,
  resolveDateObject,
  resolveId,
  resolveImage,
  resolveLink,
  resolveAsset,
  resolveReferences,
  resolveRelatedDocument,
  resolveRTC,
  resolveSpacingSize,
  resolveTheme,
} from '@/utils/resolvers';
import React, { useMemo, useRef } from 'react';
import CMSWrapper from '../shared/CMSWrapper';
import { withGTM } from '../shared/HOCs';
import SEOHrefLang from '../shared/SEOHrefLang';
import CustomerStory from './CustomerStory';
import { onDatalayerSocialShare } from './CustomerStory.datalayer';

const resolveRelatedDocumentList = ({ rawVal, page, labels = {} }: any) => {
  return rawVal.map((item: any) => resolveRelatedDocument({ rawVal: item, page, labels }));
};

const replaceCustomerLabel = ({ page, backLabel }: any) => {
  const breadCrumbCmp = getBreadcrumbComponent({ page });
  const hasBreadcrumb = Boolean(breadCrumbCmp);
  const breadcrumbData = hasBreadcrumb ? getBreadcrumbData({ breadCrumbCmp }) : undefined;
  const breadcrumbLink = hasBreadcrumb
    ? getBreadcrumbSecondLastLink({ breadcrumbData })
    : undefined;

  return hasBreadcrumb && breadcrumbLink
    ? getBackLink({ breadcrumbLink, backLabel, page })
    : undefined;
};

const MappedCustomerStory = ({
  content,
  config,
  contentButtons,
  page,
  labels,
  pageModels,
}: any) => {
  const translationConfig = pageModels?.config?.translation;
  const { relatedContent, keys } = content || {};
  const relatedContentList = useMemo(
    () => relatedContent && resolveRelatedDocumentList({ rawVal: relatedContent, page, labels }),
    [page, relatedContent, labels],
  );

  const backLink = useMemo(
    () => replaceCustomerLabel({ page, backLabel: labels?.back_to || 'Back to %1' }),
    [labels?.back_to, page],
  );

  const isElasticAllowed =
    !!content.isElasticAllowed && relatedContentList?.length < MAX_RELATED_CONTENT;

  const titleExclusion = React.useMemo(() => {
    return [
      ...(relatedContentList?.map(
        (record: any) => record?.pageMetadata?.searchDisplayTitle || record?.cardHeading,
      ) ?? []),
      content?.pageMetadata?.searchDisplayTitle || content?.title,
    ];
  }, [content?.pageMetadata?.searchDisplayTitle, content?.title, relatedContentList]);

  const { es, curatedRelatedContent, search, sortOptions, status, allLabels, engine, searchKey } =
    useElasticRelated({
      keys,
      pageModels,
      page,
      labels,
      isElasticAllowed,
      relatedContentList,
      contentCategoryFallback: ['Customer Story'],
      sortBy: 'most-relevant',
      facetsList: ['industry', 'product_line', 'product_type', 'region'],
      excludedFilters: [{ title: titleExclusion }],
    });

  const customerStoryContent = useMemo(
    () => ({
      ...content,
      relatedContent: curatedRelatedContent,
      config,
      labels,
      contentButtons,
      backLink,
    }),
    [backLink, config, content, contentButtons, curatedRelatedContent, labels],
  );
  const { introductoryContainerBackground, relatedContainerBackground } = content;
  const theme = useMemo(
    () => resolveTheme('bg-color')({ rawVal: introductoryContainerBackground }),
    [introductoryContainerBackground],
  );
  const relatedContentTheme = useMemo(
    () => resolveTheme('bg-color')({ rawVal: relatedContainerBackground }),
    [relatedContainerBackground],
  );
  const cardColor = useMemo(
    () => resolveColorName({ rawVal: relatedContentTheme || 'default' }),
    [relatedContentTheme],
  );
  const hrefLangProps = React.useMemo(
    () => ({
      hrefLangsRaw: content?.pageMetadata?.hreflangUrl,
      exclusionPaths: translationConfig?.endpoint_exclusion_paths,
      countryCodesWhitelist: translationConfig?.country_codes_whitelist,
    }),
    [
      content?.pageMetadata?.hreflangUrl,
      translationConfig?.endpoint_exclusion_paths,
      translationConfig?.country_codes_whitelist,
    ],
  );

  const contextValue = useMemo(
    () => ({
      queryParams: search,
      updateQueryParams: () => {},
      sortOptions,
      pagination: es.response.pagination,
      facets: es.facets,
      hideFilters: ['tax_content_category'],
      status,
      results: es.response.results,
      labels: allLabels,
      manualUpdate: true,
      engine,
      requestID: es.response?.meta?.request_id,
      searchKey,
    }),
    [
      search,
      sortOptions,
      es.response.pagination,
      es.facets,
      status,
      es.response.results,
      allLabels,
      engine,
      es.response?.meta?.request_id,
      searchKey,
    ],
  );

  return (
    <div data-datalayer-position="customer_story">
      <ElasticComponentContext.Provider value={contextValue}>
        <CustomerStory
          theme={theme}
          relatedContentTheme={relatedContentTheme}
          {...customerStoryContent}
          cardColor={cardColor}
        />
        <SEOHrefLang {...hrefLangProps} />
      </ElasticComponentContext.Provider>
    </div>
  );
};

const MappedSocialShareEvents = withGTM({
  Component: MappedCustomerStory,
  onDatalayer: onDatalayerSocialShare,
  config: {
    eventName: 'social-share-event',
    isCustom: true,
  },
});

const CustomerStoryCMSWrapper = (brProps: any) => {
  const mapping = useRef({
    document: [
      'content',
      {
        cta: [
          null,
          {
            ctaContent: [
              'content',
              {
                document: ['content', CTADocumentMapping],
              },
            ],
            themeBackgroundColor: ['backgroundColor', resolveColorName],
            nonThemeBackgroundColor: null,
            textColor: null,
            topContainerBackgroundColor: ['containerTopBackgroundColor', resolveColorName],
            topPadding: ['topSpacing', resolveSpacingSize],
          },
        ],
        logo: [null, resolveImage],
        headline: null,
        shortDescription: [null, resolveRTC],
        primaryButton: [null, resolveLink],
        downloadPDF: [null, resolveAsset],
        includeBreadcrumb: null,
        includeDownload: null,
        detailContent: ['detailContent', resolveContentBlocksContainer],
        pageMetadata: [null, MetadataDocumentMapping],
        introductoryContainerBackground: [null, resolveColorName],
        introductoryContainerBottomSpacing: [null, resolveSpacingSize],
        relatedContainerBackground: [null, resolveColorName],
        relatedContainerTopSpacing: [null, resolveSpacingSize],
        relatedContainerBottomSpacing: [null, resolveSpacingSize],
        relatedContent: [null, resolveReferences],
        relatedContentLink: [null, resolveLink],
        cardTileImage: ['cardImage', resolveImage],
        id: [null, resolveId],
        keys: null,
        contentcategory: 'contentCategory',
        releaseDate: [null, resolveDateObject],
        useElasticSearch: ['isElasticAllowed', resolveBool],
      },
    ],
    'customerStoryPage.config': 'config',
    'customerStoryPage.labels': 'labels',
  });

  const contentButtonNames = useRef({
    document: 'content',
  });

  return (
    <CMSWrapper
      brProps={brProps}
      render={MappedCustomerStory}
      mapping={mapping.current}
      contentButtonNames={contentButtonNames.current}
    />
  );
};

export default CustomerStoryCMSWrapper;
